import { useMemo, useState } from "react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { ChevronDown, Search, X } from "lucide-react";

/**
 * MultiSelectFilter — checkbox popover for Reports filters.
 *
 * Empty selection is treated as "All" (no filter applied). Options may
 * include a synthetic `__none__` entry so callers can compose "(No client)"
 * with real IDs in a single pick.
 */
export function MultiSelectFilter({
  options,        // [{ id, name, color? }]
  value,          // string[]  (ids currently selected; [] = All)
  onChange,       // (string[]) => void
  allLabel,       // "All members", "All clients", …
  searchable = true,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    if (!q.trim()) return options;
    const needle = q.trim().toLowerCase();
    return options.filter((o) => (o.name || "").toLowerCase().includes(needle));
  }, [options, q]);

  const { label, fullLabel } = useMemo(() => {
    if (!value || value.length === 0) return { label: allLabel, fullLabel: allLabel };
    const idToName = new Map(options.map((o) => [o.id, o.name]));
    const names = value.map((id) => idToName.get(id) || id).filter(Boolean);
    const full = names.join(", ");
    if (names.length <= 2) return { label: full, fullLabel: full };
    return { label: `${names.slice(0, 2).join(", ")} +${names.length - 2} more`, fullLabel: full };
  }, [value, options, allLabel]);

  const toggle = (id) => {
    const has = value.includes(id);
    onChange(has ? value.filter((v) => v !== id) : [...value, id]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={fullLabel}
          className="h-9 mt-1 w-full flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#145c8c]/40"
          data-testid={testId}
        >
          <span className="truncate text-left flex-1 min-w-0">{label}</span>
          {value.length > 0 && (
            <span
              role="button"
              tabIndex={0}
              onClick={(ev) => { ev.stopPropagation(); onChange([]); }}
              onKeyDown={(ev) => {
                if (ev.key === "Enter" || ev.key === " ") {
                  ev.stopPropagation();
                  ev.preventDefault();
                  onChange([]);
                }
              }}
              className="text-slate-400 hover:text-slate-600 cursor-pointer inline-flex items-center"
              aria-label="Clear selection"
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-64 p-2"
        data-testid={testId ? `${testId}-menu` : undefined}
      >
        {searchable && options.length > 6 && (
          <div className="relative mb-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="pl-7 h-8 text-sm"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
        )}
        <div className="flex items-center justify-between px-1 py-1 text-[11px] uppercase tracking-widest text-slate-400">
          <span>{value.length ? `${value.length} selected` : "None selected"}</span>
          {value.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-slate-500 hover:text-slate-700 normal-case tracking-normal text-xs"
              data-testid={testId ? `${testId}-clear-all` : undefined}
            >
              Clear
            </button>
          )}
        </div>
        <div className="max-h-64 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-2 py-6 text-center text-xs text-slate-400">No matches</div>
          ) : (
            filtered.map((o) => {
              const checked = value.includes(o.id);
              return (
                <label
                  key={o.id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 cursor-pointer text-sm"
                  data-testid={testId ? `${testId}-option-${o.id}` : undefined}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggle(o.id)}
                    className="shrink-0"
                  />
                  {o.color && (
                    <span
                      className="inline-block h-3 w-3 rounded-full border border-slate-200 shrink-0"
                      style={{ background: o.color }}
                    />
                  )}
                  <span className="truncate">{o.name}</span>
                </label>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
